<!--
  The marketing dashboard.

  Every other marketing screen is one audience or one campaign at a time, so work that was started and left
  unfinished — a plan whose templates never fired, a campaign still sitting in draft — was only findable by
  opening industries one by one. This is the sweep that says WHICH audiences still owe something.

  Organised by audience rather than by kind of problem, because that is the unit the work is done in: you open
  one industry and clear it. It NEVER does the work itself — every line links out to the screen that owns it
  (see MarketingDashboardService), which is what keeps this one cheap page load.
-->
<template>
  <v-container fluid>
    <v-card class="my-4">
      <v-card-title>
        Marketing
        <v-spacer />
        <v-btn
          icon
          :loading="loading"
          title="Refresh"
          @click="load"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-subtitle>
        Where a plan was started and not finished. Every line opens the screen that can close it.
      </v-card-subtitle>

      <v-card-text>
        <StatTiles
          :tiles="tiles"
          :loading="loading"
          sm="4"
          md="3"
        />
      </v-card-text>
    </v-card>

    <v-row>
      <v-col
        cols="12"
        lg="8"
      >
        <v-card class="mb-6">
          <v-card-title class="text-subtitle-1">
            Audience plans
            <v-spacer />
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Filter"
              dense
              outlined
              clearable
              hide-details
              style="max-width: 260px;"
            />
          </v-card-title>

          <v-card-subtitle class="text-caption">
            One card per live plan, most outstanding first.
            <span v-if="stats.audiencesWithoutPlan">
              {{ stats.audiencesWithoutPlan }} audience{{ stats.audiencesWithoutPlan === 1 ? '' : 's' }}
              have no plan yet and get no card —
              <router-link to="/marketing/audiences">see the audiences list</router-link>.
            </span>
          </v-card-subtitle>

          <v-card-text>
            <v-skeleton-loader
              v-if="loading"
              type="article, article"
            />

            <div
              v-else-if="!visible.length"
              class="text-body-2 grey--text py-6 text-center"
            >
              {{ search ? 'No audience matches that.' : 'No audience has a live plan yet.' }}
            </div>

            <v-row v-else>
              <v-col
                v-for="audience in visible"
                :key="`${audience.targetType}-${audience.targetId}`"
                cols="12"
                md="6"
              >
                <AudienceCard :audience="audience" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        lg="4"
      >
        <!-- The one forward-looking list on the page. Everything to its left is late. -->
        <v-card
          outlined
          class="mb-6"
        >
          <v-card-title class="text-subtitle-1">
            Going out this week
          </v-card-title>
          <v-card-text>
            <div
              v-if="loading"
              class="text-caption grey--text"
            >
              Looking…
            </div>
            <div
              v-else-if="!upcoming.length"
              class="text-caption grey--text"
            >
              Nothing scheduled in the next 7 days.
            </div>
            <div
              v-for="send in visibleUpcoming"
              :key="`${send.campaignId}:${send.channel}:${send.scheduledSend}`"
              class="d-flex align-center mb-3"
            >
              <v-icon
                small
                class="mr-3"
              >
                {{ send.channel === $enums.MARKETING.CHANNEL.EMAIL ? 'mdi-email-outline' : 'mdi-share-variant-outline' }}
              </v-icon>
              <div class="mr-3">
                <a
                  class="text-subtitle-2 mcsprimary--text send-link"
                  @click="openCampaign(send.campaignId)"
                >{{ send.name }}</a>
                <div class="text-caption grey--text">
                  {{ send.scheduledSend | normalDate }} · {{ send.itemCount }} item{{ send.itemCount === 1 ? '' : 's' }}
                </div>
                <!-- Who it lands in front of. A send to everyone is the one you least want to discover after the
                     fact, so it is said in words rather than left as the absence of a filter. -->
                <div
                  class="text-caption d-flex align-center"
                  :class="send.everyone ? 'warning--text' : 'grey--text'"
                  :title="audienceTitle(send)"
                >
                  <v-icon
                    x-small
                    class="mr-1"
                    :color="send.everyone ? 'warning' : 'grey'"
                  >
                    {{ send.everyone ? 'mdi-account-group' : 'mdi-filter-outline' }}
                  </v-icon>
                  {{ audienceLabel(send) }}
                </div>
              </div>
              <v-spacer />
              <v-chip
                v-if="send.needsReview"
                x-small
                label
                color="warning"
                text-color="white"
                class="flex-shrink-0"
              >
                Needs review
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import StatTiles from '@/components/common/StatTiles';
import AudienceCard from '@/components/marketing/dashboard/AudienceCard';

export default {
  name: 'MarketingDashboard',
  components: { StatTiles, AudienceCard },
  data() {
    return {
      loading: false,
      dashboard: {},
      search: ''
    };
  },
  computed: {
    stats() {
      return this.dashboard.stats || {};
    },
    audiences() {
      return this.dashboard.audiences || [];
    },
    upcoming() {
      return this.dashboard.upcoming || [];
    },
    // The server has already ordered these most-outstanding first; filtering must not disturb that.
    visible() {
      const term = (this.search || '').trim().toLowerCase();
      if (!term) return this.audiences;
      return this.audiences.filter((a) => (a.name || '').toLowerCase().indexOf(term) !== -1);
    },
    // The list and the loading/empty messages are siblings rather than v-if branches, so this holds the rows
    // back while a refresh is in flight instead of showing last load's dates under "Looking…".
    visibleUpcoming() {
      return this.loading ? [] : this.upcoming;
    },
    tiles() {
      const s = this.stats;
      const n = this.$options.filters.separator;
      return [
        { label: 'Audiences', value: n(s.audiences || 0) },
        { label: 'Planned', value: n(s.audiencesWithPlan || 0), color: 'success' },
        { label: 'No plan yet', value: n(s.audiencesWithoutPlan || 0), color: 'warning' },
        // Plans that are out of road, or nearly. The counterpart to "No plan yet": one audience has never
        // been planned, the other was planned and has quietly reached the end of what was booked.
        { label: 'Needs next round', value: n(s.plansNeedingNextRound || 0), color: 'warning' },
        { label: 'Campaigns', value: n(s.campaigns || 0) },
        { label: 'Draft campaigns', value: n(s.draftCampaigns || 0), color: 'warning' },
        { label: 'Scheduled', value: n(s.scheduledCampaigns || 0), color: 'info' },
        { label: 'Sending (7d)', value: n(s.sendingNext7Days || 0) },
        { label: 'Sent (30d)', value: n(s.sentLast30Days || 0), color: 'success' }
      ];
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.$store
        .dispatch('marketing/getDashboard')
        .then((data) => {
          this.dashboard = data || {};
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.loading = false;
        });
    },
    // Who the send goes to, in one line. "Everyone" is the server's call (DashboardUpcoming.Everyone) — an email
    // with no work_area filter, or any social post, which every follower sees — so this never re-derives the rule
    // and drifts from the actual send. Filtered sends name their audiences, the rest going into the tooltip so a
    // campaign aimed at four topics doesn't push the row three lines tall.
    audienceLabel(send) {
      if (send.everyone) {
        return send.channel === this.$enums.MARKETING.CHANNEL.EMAIL ? 'Emails everyone' : 'Posted to everyone';
      }
      const names = send.audiences || [];
      if (!names.length) return 'Filtered audience';
      if (names.length <= 2) return names.join(', ');
      return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
    },
    audienceTitle(send) {
      if (send.everyone) {
        return send.channel === this.$enums.MARKETING.CHANNEL.EMAIL
          ? 'No work-area filter — this email goes to the whole list.'
          : 'Social posts are public — every follower sees this.';
      }
      const names = send.audiences || [];
      return names.length ? `Goes to: ${names.join(', ')}` : '';
    },
    // New tab, matching NextStepList: you're working a queue and shouldn't lose your place in it.
    openCampaign(id) {
      const href = this.$router.resolve(`/marketing/campaigns/${id}`).href;
      window.open(href, '_blank', 'noopener');
    }
  }
};
</script>

<style scoped>
/* No href — it opens in a new tab through the router, so the cursor has to be said out loud. */
.send-link {
  cursor: pointer;
}
</style>
